import { LocationOn } from "@mui/icons-material";
import {
  Autocomplete,
  AutocompleteProps,
  Box,
  FormHelperText,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import parse from "autosuggest-highlight/parse";
import { debounce } from "lodash";
import {
  FC,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { useGoogleServices } from "@ryde/redux";
import {
  cleanSecondaryText,
  ICoordinate,
  IPlacePrediction,
  IValidationsYupRoot,
  Log,
  profileDtoAtom,
  SupportedLanguage,
  ValidationHelpers,
} from "@ryde/shared";

import { convertStringCoordinate, isStringCoordinate } from "./map.helpers";

interface IAddressProps {
  value: IPlacePrediction | null;
  defaultOptions?: IPlacePrediction[];
  onChange: (value: IPlacePrediction | null) => void;
  onOptionsChange?: (options: IPlacePrediction[]) => void;
  label?: string | null;
  error?: boolean;
  formValidation?: IValidationsYupRoot | null;
  isLatLngMissing?: boolean;
}

const debounceTimeout = 400;

export const Address: FC<
  IAddressProps &
    Partial<
      Omit<AutocompleteProps<IPlacePrediction, false, false, false>, "onChange">
    >
> = ({
  value,
  defaultOptions = [],
  onChange,
  onOptionsChange,
  label,
  error,
  formValidation,
  isLatLngMissing,
  ...props
}) => {
  const { t } = useTranslation();
  const profile = useRecoilValue(profileDtoAtom);
  const { searchAddressByGeo, searchPlacesByInput } = useGoogleServices();
  const [inputValue, setInputValue] = useState(value?.text?.text ?? "");
  const [options, setOptions] = useState<IPlacePrediction[]>(defaultOptions);

  const handleOptionsChange = useCallback(
    (newOptions: IPlacePrediction[]) => {
      setOptions(newOptions);
      onOptionsChange?.(newOptions);
    },
    [onOptionsChange],
  );
  const callbackResolver = useCallback(
    async (results: IPlacePrediction[] | null): Promise<void> => {
      let newOptions: IPlacePrediction[] = [];

      if (value) {
        newOptions = [value];
      }

      if (results) {
        newOptions = [...newOptions, ...results];
      }

      handleOptionsChange(newOptions);
    },
    [handleOptionsChange, value],
  );

  const fetch = useMemo(
    () =>
      debounce(
        async (request: {
          input: string;
          language: SupportedLanguage | undefined;
        }): Promise<void> => {
          const result = await searchPlacesByInput({ inputParams: request });
          if (result?.success) {
            callbackResolver(result?.data ?? []);
          } else {
            Log.error("Fetch error", result?.error);
          }
        },
        debounceTimeout,
      ),
    [searchPlacesByInput, callbackResolver],
  );

  const fetchByLatLngCoordinate = useMemo(
    () =>
      debounce(
        async (geoParams: {
          location: ICoordinate;
          language: SupportedLanguage | undefined;
        }): Promise<void> => {
          const result = await searchAddressByGeo({ geoParams });
          if (result?.success) {
            const predictions = result?.data ? [result?.data] : [];
            callbackResolver(predictions);
          } else {
            Log.error("Fetch error", result?.error);
          }
        },
        debounceTimeout,
      ),
    [callbackResolver, searchAddressByGeo],
  );

  const handleChange = (
    _: unknown,
    newValue: IPlacePrediction | null,
  ): void => {
    handleOptionsChange(newValue ? [newValue, ...options] : []);
    onChange(newValue);
  };

  const onInputChange = async (event: SyntheticEvent, newValue: string) => {
    setInputValue(newValue);
  };

  useEffect(() => {
    const isCoordinate = isStringCoordinate(inputValue);

    if (isCoordinate) {
      fetchByLatLngCoordinate({
        location: convertStringCoordinate(inputValue) as ICoordinate,
        language: profile?.metadata?.language,
      });
    } else {
      fetch({
        input: inputValue,
        language: profile?.metadata?.language,
      });
    }
  }, [
    value,
    inputValue,
    handleOptionsChange,
    fetchByLatLngCoordinate,
    profile?.metadata?.language,
    fetch,
  ]);

  return (
    <>
      <Autocomplete
        getOptionLabel={option =>
          typeof option === "string"
            ? cleanSecondaryText(option)
            : cleanSecondaryText(option?.text?.text ?? "")
        }
        clearOnBlur={false}
        value={value}
        inputValue={inputValue}
        options={options}
        autoComplete
        // open
        includeInputInList
        filterSelectedOptions
        noOptionsText={t("rideMap.NO_LOCATIONS")}
        filterOptions={e => e}
        {...props}
        onInputChange={onInputChange}
        renderInput={params => (
          <TextField
            {...params}
            data-e2e="address-map-field"
            label={label || t("rideMap.ADD_LOCATION")}
            fullWidth
            error={error}
            required={ValidationHelpers.hasRequiredRule(formValidation)}
          />
        )}
        renderOption={(renderProps, option) => {
          const { key, ...rest } = renderProps;
          const matches = option.mainText?.matches || [];

          const parts = parse(
            option.mainText?.text ?? "",
            matches.map(match => [match.startOffset, match.endOffset]),
          );
          return (
            <li key={key} {...rest}>
              <Grid sx={{ display: "flex", width: 44 }}>
                <LocationOn sx={{ color: "text.secondary" }} />
              </Grid>
              <Grid sx={{ width: "calc(100% - 44px)", wordWrap: "break-word" }}>
                {parts.map((part, index) => (
                  <Box
                    key={index}
                    component="span"
                    sx={{ fontWeight: part.highlight ? "bold" : "regular" }}
                  >
                    {part.text}
                  </Box>
                ))}
                <Typography variant="body2" color="text.secondary">
                  {cleanSecondaryText(option.secondaryText?.text ?? "")}
                </Typography>
              </Grid>
            </li>
          );
        }}
        onChange={handleChange}
      />
      {isLatLngMissing && (
        <FormHelperText error data-e2e="address-missing-Coordinates-error">
          {t("errors.noCoordinatesForLocation", {
            fullAddress: value?.text?.text,
          })}
        </FormHelperText>
      )}
    </>
  );
};
